<template>
  <div
    class="page-container"
    :class="{ 'is-detail': viewMode === 'detail', 'is-submitting': submitting }"
  >
    <header class="header">
      <el-button text class="back-btn" aria-label="返回" @click="handleBack">
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </el-button>
      <h1 class="title">
        {{ viewMode === "list" ? "TCL扫码收货" : "扫码收货" }}
      </h1>
      <div class="header-right"></div>
    </header>

    <!-- 列表：扫描送货单条形码 -->
    <main v-if="viewMode === 'list'" class="content">
      <el-segmented
        v-model="inType"
        :options="inTypeTabOptions"
        block
        class="in-type-tabs"
        :disabled="listLoading"
        @change="handleInTypeChange"
      />

      <el-card shadow="never" class="block-card compact-card">
        <el-row :gutter="8" align="middle">
          <el-col :span="18">
            <el-input
              ref="sendInputRef"
              v-model="scanCode"
              size="large"
              placeholder="扫描送货单条形码"
              clearable
              :disabled="listLoading"
              @keydown.enter.prevent="handleListScanEnter"
              @clear="focusSendInput"
            />
          </el-col>
          <el-col :span="6">
            <el-button
              type="primary"
              size="large"
              class="full-btn"
              :disabled="listLoading || (!scannedWareInNo && list.length === 0)"
              @click="handleListReset"
            >
              重置
            </el-button>
          </el-col>
        </el-row>
      </el-card>

      <div
        v-loading="listLoading"
        element-loading-text="查询中..."
        class="list-wrap"
      >
        <el-empty
          v-if="!hasScanned && !listLoading"
          description="请用 PDA 扫描送货单条形码"
        />
        <el-empty
          v-else-if="hasScanned && list.length === 0 && !listLoading"
          :description="
            inType === 1 ? '未查到待收货退货入库单' : '未查到待收货入库单'
          "
        />

        <el-space
          v-else
          direction="vertical"
          fill
          :size="8"
          style="width: 100%"
        >
          <el-card
            v-for="item in list"
            :key="item.id"
            shadow="never"
            class="compact-card order-card"
            @click="enterDetail(item)"
          >
            <div class="order-head">
              <el-tag size="small" type="warning" effect="light">{{
                inTypeLabel
              }}</el-tag>
              <el-text tag="b" class="send-no">
                {{ item.wareInNo || scannedWareInNo || "-" }}
              </el-text>
            </div>
            <el-text size="small" type="info" class="order-meta">
              {{ item.supplierName || "-" }}
              ·
              {{ item.storeName || item.wareName || "-" }}
            </el-text>
            <el-text size="small" type="info" class="order-meta">
              创建时间：{{ formatTime(item.createTime) }}
            </el-text>
            <div class="order-footer">
              <el-text size="small">
                物料数：{{
                  item.wareMaterialNum || item.wareInDetailList?.length || 0
                }}
              </el-text>
              <el-button type="primary" size="small" round>
                扫码收货
              </el-button>
            </div>
          </el-card>
        </el-space>
      </div>
    </main>

    <!-- 详情：上部固定，明细单独滑动 -->
    <main
      v-else
      v-loading="detailLoading"
      element-loading-text="加载详情..."
      class="content detail-content"
    >
      <div class="detail-fixed">
        <el-card shadow="never" class="block-card compact-card">
          <div class="scan-row">
            <el-input
              ref="materialInputRef"
              v-model="materialCode"
              class="box-input"
              size="large"
              placeholder="扫描物料二维码"
              clearable
              :disabled="detailLoading || submitting || scanChecking"
              @keydown.enter.prevent="handleMaterialScanEnter"
              @clear="focusMaterialInput"
            />
            <el-tag size="small" effect="plain" class="scan-count-tag">
              已扫 {{ scannedBoxCount }} 箱
            </el-tag>
          </div>
        </el-card>

        <el-card
          v-if="detail"
          shadow="never"
          class="block-card compact-card info-card"
        >
          <div class="info-row">
            <span class="label">供应商</span>
            <span class="value">{{ detail.supplierName || "-" }}</span>
          </div>
          <div class="info-row">
            <span class="label">入库单号</span>
            <span class="value">{{ detail.wareInNo || "-" }}</span>
          </div>
          <div class="info-row">
            <span class="label">创建时间</span>
            <span class="value">{{ formatTime(detail.createTime) }}</span>
          </div>
          <div v-if="detail.receiptNo" class="info-row">
            <span class="label">送货单号</span>
            <span class="value">{{ detail.receiptNo }}</span>
          </div>
        </el-card>

        <div class="section-title-row detail-list-head">
          <span class="section-title">入库明细</span>
          <el-button
            v-if="hasUnlockedBoxes"
            type="danger"
            text
            size="small"
            @click="clearScanned"
          >
            清空已扫
          </el-button>
        </div>
      </div>

      <div class="detail-scroll">
        <el-empty
          v-if="compareList.length === 0 && !detailLoading"
          description="暂无明细"
          :image-size="48"
        />
        <div v-else class="compare-list">
          <div
            v-for="(row, index) in compareList"
            :key="row.materialNo"
            class="compare-item"
            :class="row.status"
            @click="openBoxDialog(row)"
          >
            <span class="mat-idx">{{ index + 1 }}</span>
            <span class="mat-no" :title="row.materialNo">{{
              row.materialNo
            }}</span>
            <span class="mat-qty">
              {{ formatQty(row.scannedQty) }}/{{ formatQty(row.expectQty) }}
              ·{{ row.boxCount }}箱
            </span>
            <el-tag
              size="small"
              :type="statusTagType(row.status)"
              effect="light"
              class="mat-tag"
            >
              {{ row.hint || statusLabel(row.status) }}
            </el-tag>
          </div>
        </div>
      </div>

      <div class="footer-actions">
        <el-button
          type="primary"
          size="large"
          class="submit-btn"
          :loading="submitting"
          :disabled="
            detailLoading || scannedBoxes.length === 0 || hasOverQty
          "
          @click="handleSubmit"
        >
          {{
            submitting
              ? "正在入库中..."
              : hasOverQty
              ? "数量超出，请改箱码数量"
              : "确认入库"
          }}
        </el-button>
      </div>
    </main>

    <!-- 物料箱码弹窗：统一列表，支持搜码改数 -->
    <el-dialog
      v-model="boxDialogVisible"
      :title="boxDialogTitle"
      width="92%"
      align-center
      class="box-dialog"
      @closed="onBoxDialogClosed"
    >
      <div class="box-dialog-summary" :class="boxDialogSummary.status">
        <div class="sum-item">
          <span class="sum-label">应入</span>
          <span class="sum-val">{{ formatQty(boxDialogSummary.expectQty) }}</span>
        </div>
        <div class="sum-item">
          <span class="sum-label">已扫</span>
          <span class="sum-val">{{ formatQty(boxDialogSummary.scannedQty) }}</span>
        </div>
        <div class="sum-item">
          <span class="sum-label">箱数</span>
          <span class="sum-val">{{ boxDialogSummary.boxCount }}</span>
        </div>
        <div class="sum-item sum-diff">
          <span class="sum-label">差额</span>
          <span class="sum-val">{{ boxDialogSummary.diffText }}</span>
        </div>
      </div>
      <div class="box-dialog-search">
        <el-input
          ref="boxSearchInputRef"
          v-model="boxDialogSearch"
          size="large"
          clearable
          placeholder="输入或扫描箱码搜索后改数量"
          @keyup.enter="handleBoxDialogSearch"
          @clear="boxDialogHighlightIdx = -1"
        />
      </div>
      <div class="box-dialog-scroll">
        <div class="dialog-section">
          <div class="dialog-section-title">
            箱码（{{ filteredBoxDialogBoxes.length }}/{{
              boxDialogBoxes.length
            }}）
          </div>
          <el-empty
            v-if="filteredBoxDialogBoxes.length === 0"
            :description="
              boxDialogBoxes.length === 0 ? '暂无箱码' : '未找到匹配箱码'
            "
            :image-size="48"
          />
          <div v-else class="box-list">
            <div
              v-for="box in filteredBoxDialogBoxes"
              :id="'in-box-row-' + box._idx"
              :key="box.barCode + '-' + box._idx"
              class="box-item"
              :class="{
                locked: box.fromOrder,
                highlight: boxDialogHighlightIdx === box._idx,
              }"
            >
              <el-tag
                v-if="box.fromOrder"
                size="small"
                type="info"
                effect="plain"
                class="box-lock-tag"
              >
                单据
              </el-tag>
              <el-tag
                v-else-if="isSplitBox(scannedBoxes[box._idx])"
                type="warning"
                size="small"
                effect="dark"
                class="split-tag"
              >
                拆
              </el-tag>
              <span class="box-code" :title="box.barCode">
                <bdi>{{ box.barCode }}</bdi>
              </span>
              <span v-if="box.fromOrder" class="box-qty">
                ×{{ formatQty(box.wareOutNum) }}
              </span>
              <el-input-number
                v-else
                :ref="(el) => setBoxQtyRef(box._idx, el)"
                v-model="scannedBoxes[box._idx].wareOutNum"
                class="box-qty-input"
                size="small"
                :min="0.001"
                :step="1"
                :precision="3"
                controls-position="right"
                :disabled="submitting"
                @change="(val) => handleBoxQtyChange(box._idx, val)"
              />
              <el-button
                v-if="!box.fromOrder"
                text
                type="danger"
                size="small"
                class="box-del"
                :disabled="submitting"
                @click="removeBox(box._idx)"
              >
                删
              </el-button>
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <el-button type="primary" @click="boxDialogVisible = false">
          关闭
        </el-button>
      </template>
    </el-dialog>

    <!-- 入库核对异常弹窗（紧凑可滑动） -->
    <el-dialog
      v-model="mismatchDialogVisible"
      title="入库核对异常"
      width="92%"
      align-center
      class="mismatch-dialog"
      :close-on-click-modal="false"
    >
      <div class="mismatch-summary">
        共 {{ mismatchList.length }} 项异常，确认仍要入库？
      </div>
      <div class="mismatch-scroll">
        <div
          v-for="(row, idx) in mismatchList"
          :key="row.materialNo + '-' + idx"
          class="mismatch-item"
          :class="row.type"
        >
          <div class="mismatch-top">
            <span class="mismatch-idx">{{ idx + 1 }}</span>
            <span class="mismatch-no" :title="row.materialNo">{{
              row.materialNo
            }}</span>
            <el-tag
              size="small"
              :type="mismatchTagType(row.type)"
              effect="light"
            >
              {{ row.typeLabel }}
            </el-tag>
          </div>
          <div class="mismatch-qty">
            应入 {{ formatQty(row.expectQty) }}
            <span class="qty-sep">/</span>
            已扫 {{ formatQty(row.scannedQty) }}
            <span v-if="row.diffText" class="qty-diff">{{ row.diffText }}</span>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="mismatch-footer">
          <el-button @click="mismatchDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="confirmMismatchAndSubmit">
            仍要入库
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 入库中全屏遮罩 -->
    <teleport to="body">
      <div v-if="submitting" class="submit-overlay">
        <div class="submit-panel">
          <el-icon class="submit-spinner" :size="42">
            <Loading />
          </el-icon>
          <p class="submit-text">正在入库中</p>
          <p class="submit-sub">请稍候，勿重复操作</p>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from "vue";
import { useRouter } from "vue-router";
import { ElMessage, ElMessageBox } from "element-plus";
import { Loading } from "@element-plus/icons-vue";
import {
  getTclWareInPage,
  getTclWareInDetail,
  checkBarCodeWareIn,
  tclWareInCollect,
} from "@/api/tclScanReceive";
import {
  unlockScanAudio,
  playScanSuccessBeep,
  playScanErrorBeep,
  closeScanAudio,
} from "@/utils/scanBeep";

const router = useRouter();

const viewMode = ref("list"); // list | detail
const listLoading = ref(false);
const detailLoading = ref(false);
const submitting = ref(false);
const scanChecking = ref(false);

/** inType: 0=收货入库，1=退货入库；进入默认收货入库 */
const inType = ref(0);
const inTypeTabOptions = [
  { label: "收货入库", value: 0 },
  { label: "退货入库", value: 1 },
];
const inTypeLabel = computed(() =>
  Number(inType.value) === 1 ? "退货入库" : "收货入库"
);

const scanCode = ref("");
const scannedWareInNo = ref("");
const hasScanned = ref(false);
const list = ref([]);

const detail = ref(null);
const materialList = ref([]); // 单据明细
const materialCode = ref("");
/** fromOrder=true：详情带回的单据箱码，不可删、不可改数量 */
const scannedBoxes = ref([]); // { barCode, materialNo, materialId, wareOutNum, fromOrder }

const sendInputRef = ref(null);
const materialInputRef = ref(null);

const boxDialogVisible = ref(false);
const boxDialogMaterialNo = ref("");
const boxDialogSearch = ref("");
const boxDialogHighlightIdx = ref(-1);
const boxSearchInputRef = ref(null);
const boxQtyInputRefs = ref({});
const boxDialogTitle = computed(() =>
  boxDialogMaterialNo.value
    ? `箱码信息 · ${boxDialogMaterialNo.value}`
    : "箱码信息"
);

const scannedBoxCount = computed(() => scannedBoxes.value.length);
const hasUnlockedBoxes = computed(() =>
  scannedBoxes.value.some((b) => !b.fromOrder)
);
const hasOverQty = computed(() =>
  compareList.value.some((row) => row.status === "over")
);

function setBoxQtyRef(idx, el) {
  if (el) boxQtyInputRefs.value[idx] = el;
  else delete boxQtyInputRefs.value[idx];
}

/** 从明细行提取有效单据箱码（有 barCode 的记录） */
function extractOrderBoxesFromDetail(detailRow) {
  const materialNo = String(detailRow?.materialNo || "").trim();
  const materialId = detailRow?.materialId ?? null;
  const rawList =
    detailRow?.boxList ||
    detailRow?.recordVOList ||
    detailRow?.wareInDetailRecordVOList ||
    [];
  if (!Array.isArray(rawList)) return [];

  const result = [];
  const seen = new Set();
  for (const b of rawList) {
    const barCode = String(b?.barCode || b?.boxCode || "").trim();
    if (!barCode || seen.has(barCode)) continue;
    seen.add(barCode);
    const qty = b.wareOutNum ?? b.wareInNum ?? b.num ?? 0;
    result.push({
      barCode,
      materialNo,
      materialId,
      wareOutNum: Number(qty) || 0,
      fromOrder: true,
      raw: b,
    });
  }
  return result;
}

/** 进入详情时，把单据已有箱码预载进统一列表 */
function seedOrderBoxes(details) {
  const boxes = [];
  for (const row of Array.isArray(details) ? details : []) {
    boxes.push(...extractOrderBoxesFromDetail(row));
  }
  return boxes;
}

const boxDialogBoxes = computed(() => {
  const no = String(boxDialogMaterialNo.value || "").trim();
  return scannedBoxes.value
    .map((box, idx) => ({ ...box, _idx: idx }))
    .filter((box) => String(box.materialNo || "").trim() === no);
});

const filteredBoxDialogBoxes = computed(() => {
  const kw = String(boxDialogSearch.value || "")
    .replace(/[\r\n\t]/g, "")
    .trim()
    .toLowerCase();
  if (!kw) return boxDialogBoxes.value;
  const parts = kw.split("*").filter(Boolean);
  const codeKw = parts.length > 1 ? parts[parts.length - 1] : kw;
  return boxDialogBoxes.value.filter((box) =>
    String(box.barCode || "")
      .toLowerCase()
      .includes(codeKw)
  );
});

/** 弹窗顶部汇总：改数量时实时对照应入数量 */
const boxDialogSummary = computed(() => {
  const no = String(boxDialogMaterialNo.value || "").trim();
  const row = compareList.value.find(
    (r) => String(r.materialNo || "").trim() === no
  );
  const expectQty = Number(row?.expectQty) || 0;
  const scannedQty = Number(row?.scannedQty) || 0;
  const boxCount = Number(row?.boxCount) || boxDialogBoxes.value.length;
  const status = row?.status || "pending";
  let diffText = "待扫";
  if (status === "ok") diffText = "已齐";
  else if (status === "short")
    diffText = `少${formatQty(expectQty - scannedQty)}`;
  else if (status === "over")
    diffText = `多${formatQty(scannedQty - expectQty)}`;
  else if (scannedQty > 0) diffText = formatQty(scannedQty - expectQty);
  return { expectQty, scannedQty, boxCount, status, diffText };
});

/** 按物料编码汇总已扫数量 */
const scannedAggMap = computed(() => {
  const map = new Map();
  for (const box of scannedBoxes.value) {
    const key = String(box.materialNo || "").trim();
    if (!key) continue;
    const prev = map.get(key) || { qty: 0, boxCount: 0, materialId: box.materialId };
    prev.qty += Number(box.wareOutNum) || 0;
    prev.boxCount += 1;
    if (box.materialId != null) prev.materialId = box.materialId;
    map.set(key, prev);
  }
  return map;
});

/**
 * 核对列表（仅单据明细；非明细编码扫码时直接拦截，不加入）
 * status: ok | short | over | pending
 */
const compareList = computed(() => {
  const result = [];

  for (const m of materialList.value) {
    const no = String(m.materialNo || "").trim();
    if (!no) continue;
    const expectQty = Number(m.wareInNum ?? m.comeNum ?? 0) || 0;
    const agg = scannedAggMap.value.get(no);
    const scannedQty = agg?.qty || 0;
    const boxCount = agg?.boxCount || 0;

    let status = "pending";
    let hint = "";
    if (scannedQty === 0) {
      status = "pending";
      hint = "";
    } else if (almostEqual(scannedQty, expectQty)) {
      status = "ok";
      hint = "";
    } else if (scannedQty < expectQty) {
      status = "short";
      hint = `少${formatQty(expectQty - scannedQty)}`;
    } else {
      status = "over";
      hint = `多${formatQty(scannedQty - expectQty)}`;
    }

    result.push({
      materialId: m.materialId,
      materialNo: no,
      materialDesc: m.materialDesc || "",
      expectQty,
      scannedQty,
      boxCount,
      status,
      hint,
    });
  }

  return result;
});

function almostEqual(a, b, eps = 0.0001) {
  return Math.abs(Number(a) - Number(b)) < eps;
}

function formatQty(val) {
  if (val === null || val === undefined || val === "") return "-";
  const n = Number(val);
  if (!Number.isFinite(n)) return String(val);
  if (Number.isInteger(n)) return String(n);
  return String(parseFloat(n.toFixed(6)));
}

function formatTime(val) {
  if (!val) return "-";
  const raw = typeof val === "string" ? val : String(val);
  const safe =
    raw.includes(" ") && !raw.includes("T") ? raw.replace(" ", "T") : raw;
  const d = new Date(safe);
  if (Number.isNaN(d.getTime())) return String(val);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
}

function statusLabel(status) {
  const map = {
    ok: "一致",
    pending: "待扫",
    short: "少扫",
    over: "多扫",
    extra: "多余",
    missing: "缺失",
  };
  return map[status] || status;
}

function statusTagType(status) {
  const map = {
    ok: "success",
    pending: "info",
    short: "warning",
    over: "danger",
    extra: "danger",
    missing: "danger",
  };
  return map[status] || "info";
}

function normalizeScanText(raw) {
  return String(raw || "")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/[\r\n\t]/g, "")
    .trim();
}

// 嘈杂仓环境：高频双叮成功 / 四拍方波蜂鸣异常（见 utils/scanBeep）
const unlockAudio = () => unlockScanAudio();
const playSuccessBeep = () => playScanSuccessBeep();
const playErrorBeep = () => playScanErrorBeep();

/**
 * 解析 TCL 物料二维码
 * 格式：41105-000139*33*130322*20260718*0*0*0×0×0***B1303222629000609
 * 物料编码=第1段，数量=第2段，箱码=按 * 分割后最后一串非空字符（不一定 B 开头）
 */
function parseTclMaterialQr(raw) {
  const text = normalizeScanText(raw);
  if (!text) return null;

  const parts = text.split("*").map((p) => String(p || "").trim());
  const materialNo = parts[0] || "";
  const qty = Number(parts[1]);

  // 取最后一串非空内容作为 barCode
  let barCode = "";
  for (let i = parts.length - 1; i >= 2; i--) {
    if (parts[i]) {
      barCode = parts[i];
      break;
    }
  }

  if (!materialNo) return null;
  if (!Number.isFinite(qty) || qty <= 0) return null;
  if (!barCode) return null;

  return { materialNo, qty, barCode, raw: text };
}

const focusSendInput = async () => {
  await nextTick();
  const inputComp = sendInputRef.value;
  const inputEl = inputComp?.$el?.querySelector?.("input");
  if (inputEl) inputEl.focus();
  else if (typeof inputComp?.focus === "function") inputComp.focus();
};

const focusMaterialInput = async () => {
  await nextTick();
  const inputComp = materialInputRef.value;
  const inputEl = inputComp?.$el?.querySelector?.("input");
  if (inputEl) inputEl.focus();
  else if (typeof inputComp?.focus === "function") inputComp.focus();
};

/** 从事件 / 原生 input / v-model 取扫码值（兼容 PDA 回车早于 v-model 更新） */
function resolveListScanCode(e) {
  const fromEvent = e?.target?.value;
  const nativeEl = sendInputRef.value?.$el?.querySelector?.("input");
  const fromNative = nativeEl?.value;
  return normalizeScanText(fromEvent || fromNative || scanCode.value);
}

const fetchByWareInNo = async (wareInNo) => {
  const code = normalizeScanText(wareInNo);
  if (!code) {
    focusSendInput();
    return;
  }
  // 查询中或刚扫过同一单号，忽略 PDA 二次回车
  if (listLoading.value) return;
  if (scannedWareInNo.value === code && list.value.length > 0) {
    scanCode.value = "";
    return;
  }

  listLoading.value = true;
  hasScanned.value = true;
  scannedWareInNo.value = code;
  scanCode.value = "";

  try {
    const res = await getTclWareInPage({
      wareInNo: code,
      inType: Number(inType.value) === 1 ? 1 : 0,
      source: 0,
      wareInStatus: 0,
      current: 1,
      size: 20,
    });
    const records = res?.data?.records || [];
    list.value = records;
    if (records.length === 0) {
      ElMessage.info(
        Number(inType.value) === 1
          ? "未查询到待收货退货入库单"
          : "未查询到待收货入库单"
      );
    }
    // 扫码只查列表，不自动进详情、不调 detail 接口
  } catch (e) {
    console.error("TCL收货列表查询失败:", e);
    list.value = [];
    ElMessage.error(e?.message || "查询失败，请重试");
  } finally {
    listLoading.value = false;
    focusSendInput();
  }
};

const handleInTypeChange = () => {
  list.value = [];
  hasScanned.value = false;
  if (scannedWareInNo.value) {
    fetchByWareInNo(scannedWareInNo.value);
  } else {
    focusSendInput();
  }
};

const handleListScanEnter = (e) => {
  if (listLoading.value) return;
  const code = resolveListScanCode(e);
  // PDA 常在成功后多发一次空回车，忽略空值，避免误报「请先扫描」
  if (!code) return;
  unlockAudio();
  fetchByWareInNo(code);
};

const handleListReset = () => {
  scanCode.value = "";
  scannedWareInNo.value = "";
  hasScanned.value = false;
  list.value = [];
  focusSendInput();
};

/** 点击「扫码收货」才请求详情 */
const enterDetail = async (item) => {
  if (!item) {
    ElMessage.error("单据数据为空");
    return;
  }
  const id = item?.id || item?.wareInId;
  if (!id) {
    ElMessage.error("入库单缺少 ID");
    return;
  }

  viewMode.value = "detail";
  detailLoading.value = true;
  detail.value = null;
  materialList.value = [];
  scannedBoxes.value = [];
  materialCode.value = "";
  boxDialogVisible.value = false;

  try {
    const res = await getTclWareInDetail(id);
    if (res?.code !== 200 || !res?.data) {
      throw new Error(res?.msg || "获取入库单详情失败");
    }
    detail.value = res.data;
    const details = Array.isArray(res.data.wareInDetailList)
      ? res.data.wareInDetailList
      : [];
    materialList.value = details;
    scannedBoxes.value = seedOrderBoxes(details);
    if (details.length === 0) {
      ElMessage.warning("该入库单暂无物料明细");
    } else if (scannedBoxes.value.length > 0) {
      ElMessage.success(
        `已载入单据箱码 ${scannedBoxes.value.length} 箱，可继续扫码补充`
      );
    }
  } catch (e) {
    console.error("加载详情失败:", e);
    ElMessage.error(e?.message || "加载详情失败");
    viewMode.value = "list";
  } finally {
    detailLoading.value = false;
    focusMaterialInput();
  }
};

const openBoxDialog = async (row) => {
  boxDialogMaterialNo.value = String(row?.materialNo || "").trim();
  boxDialogSearch.value = "";
  boxDialogHighlightIdx.value = -1;
  boxDialogVisible.value = true;
  await nextTick();
  const inputComp = boxSearchInputRef.value;
  if (typeof inputComp?.focus === "function") inputComp.focus();
  else {
    const inputEl = inputComp?.$el?.querySelector?.("input");
    inputEl?.focus?.();
  }
};

const onBoxDialogClosed = () => {
  boxDialogSearch.value = "";
  boxDialogHighlightIdx.value = -1;
  focusMaterialInput();
};

const handleBoxDialogSearch = async () => {
  const kw = String(boxDialogSearch.value || "")
    .replace(/[\r\n\t]/g, "")
    .trim();
  if (!kw) {
    boxDialogHighlightIdx.value = -1;
    return;
  }
  const parts = kw.split("*").filter(Boolean);
  const codeKw = (parts.length > 1 ? parts[parts.length - 1] : kw).toLowerCase();
  const list = boxDialogBoxes.value;
  const hit =
    list.find(
      (b) => String(b.barCode || "").toLowerCase() === codeKw
    ) ||
    list.find((b) =>
      String(b.barCode || "")
        .toLowerCase()
        .includes(codeKw)
    );
  if (!hit) {
    playErrorBeep();
    ElMessage.warning(`未找到箱码「${codeKw}」`);
    boxDialogHighlightIdx.value = -1;
    return;
  }
  boxDialogSearch.value = hit.barCode;
  boxDialogHighlightIdx.value = hit._idx;
  await nextTick();
  document
    .getElementById(`in-box-row-${hit._idx}`)
    ?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  if (!hit.fromOrder) {
    const qtyInput = boxQtyInputRefs.value[hit._idx]?.$el?.querySelector?.(
      "input"
    );
    qtyInput?.focus?.();
    qtyInput?.select?.();
  } else {
    ElMessage.info("单据箱码数量不可改，仅可查看");
  }
};

/** 明细扫码取值：事件 / 原生 input / v-model，兼容 PDA 二次回车 */
function resolveMaterialScanCode(e) {
  const fromEvent = e?.target?.value;
  const nativeEl = materialInputRef.value?.$el?.querySelector?.("input");
  const fromNative = nativeEl?.value;
  return normalizeScanText(fromEvent || fromNative || materialCode.value);
}

const handleMaterialScanEnter = (e) => {
  if (detailLoading.value || submitting.value || scanChecking.value) return;
  const raw = resolveMaterialScanCode(e);
  // PDA 常在成功后多发一次空回车，忽略空值，避免误报解析失败
  if (!raw) return;
  unlockAudio();
  materialCode.value = "";
  processMaterialScan(raw);
};

const processMaterialScan = async (raw) => {
  const text = normalizeScanText(raw);
  if (!text) {
    focusMaterialInput();
    return;
  }
  const parsed = parseTclMaterialQr(text);
  if (!parsed) {
    playErrorBeep();
    ElMessage.error("物料二维码解析失败，请检查格式");
    focusMaterialInput();
    return;
  }

  // 非明细编码：提示异常，不加入
  const matched = materialList.value.find(
    (m) => String(m.materialNo || "").trim() === parsed.materialNo
  );
  if (!matched) {
    playErrorBeep();
    ElMessage.error(`异常：编码「${parsed.materialNo}」不在入库明细中`);
    focusMaterialInput();
    return;
  }

  if (matched.materialId == null || matched.materialId === "") {
    playErrorBeep();
    ElMessage.error(`异常：物料「${parsed.materialNo}」缺少物料ID，无法校验箱码`);
    focusMaterialInput();
    return;
  }

  // 箱码（二维码最后一串）不可重复扫描
  const dup = scannedBoxes.value.some(
    (b) => String(b.barCode) === String(parsed.barCode)
  );
  if (dup) {
    playErrorBeep();
    ElMessage.error(`异常：箱码「${parsed.barCode}」已扫描，不可重复扫`);
    focusMaterialInput();
    return;
  }

  // 服务端校验：箱码是否已入库（0=未入库可扫，>0=已入库不可扫）
  scanChecking.value = true;
  try {
    const res = await checkBarCodeWareIn(matched.materialId, parsed.barCode);
    if (res?.success === false || (res?.code != null && res.code !== 200)) {
      throw new Error(res?.msg || res?.message || "箱码入库校验失败");
    }
    const count = Number(res?.data ?? 0);
    if (Number.isFinite(count) && count > 0) {
      playErrorBeep();
      ElMessage.error(
        `异常：箱码「${parsed.barCode}」已入库，不能重复入库`
      );
      focusMaterialInput();
      return;
    }
  } catch (e) {
    console.error("箱码入库校验失败:", e);
    playErrorBeep();
    ElMessage.error(e?.message || "箱码入库校验失败");
    focusMaterialInput();
    return;
  } finally {
    scanChecking.value = false;
  }

  // 默认取二维码数量（不自动按剩余需求拆包）
  const finalQty = Number(parsed.qty) || 0;
  const expectQty = Number(matched.wareInNum ?? matched.comeNum ?? 0) || 0;
  const alreadyScanned = scannedBoxes.value
    .filter((b) => String(b.materialNo || "").trim() === parsed.materialNo)
    .reduce((sum, b) => sum + (Number(b.wareOutNum) || 0), 0);
  const remaining = expectQty - alreadyScanned;
  const willOver = expectQty > 0 && finalQty > remaining + 0.0001;

  // 新扫箱码插到前面
  scannedBoxes.value.unshift({
    barCode: parsed.barCode,
    materialNo: parsed.materialNo,
    materialId: matched.materialId ?? null,
    wareOutNum: finalQty,
    qrQty: finalQty,
    isSplit: false,
    fromOrder: false,
    raw: parsed.raw,
  });

  playSuccessBeep();
  if (willOver) {
    ElMessage.warning(
      `已按扫码数量 ${formatQty(finalQty)} 计入，将超出需求（剩余 ${formatQty(
        Math.max(remaining, 0)
      )}），请手动点明细改箱码数量后再入库`
    );
  } else {
    ElMessage.success(
      `已扫 ${parsed.materialNo} × ${formatQty(finalQty)}（${parsed.barCode}）`
    );
  }
  focusMaterialInput();
};

/** 人工改到小于扫码数量 → 拆包 */
function isSplitBox(box) {
  if (!box || box.fromOrder) return false;
  const qr = Number(box.qrQty);
  const cur = Number(box.wareOutNum);
  if (!Number.isFinite(qr) || !Number.isFinite(cur)) return !!box.isSplit;
  return cur < qr - 0.0001;
}

const handleBoxQtyChange = (idx, val) => {
  const box = scannedBoxes.value[idx];
  if (!box) return;
  if (box.fromOrder) {
    ElMessage.warning("单据箱码数量不允许修改");
    return;
  }
  let next = Number(val);
  if (!Number.isFinite(next) || next <= 0) {
    ElMessage.warning("数量必须大于 0");
    box.wareOutNum = box.wareOutNum > 0 ? box.wareOutNum : 0.001;
    box.isSplit = isSplitBox(box);
    return;
  }
  box.wareOutNum = next;
  box.isSplit = isSplitBox(box);
};

const removeBox = (idx) => {
  const box = scannedBoxes.value[idx];
  if (!box || box.fromOrder) {
    ElMessage.warning("单据箱码不允许删除");
    return;
  }
  scannedBoxes.value.splice(idx, 1);
};

const clearScanned = async () => {
  if (!hasUnlockedBoxes.value) {
    ElMessage.warning("没有可清空的已扫箱码（单据箱码不可清空）");
    return;
  }
  try {
    await ElMessageBox.confirm(
      "确定清空本次新扫箱码？单据箱码会保留。",
      "提示",
      {
        type: "warning",
        confirmButtonText: "清空",
        cancelButtonText: "取消",
      }
    );
    scannedBoxes.value = scannedBoxes.value.filter((b) => b.fromOrder);
    focusMaterialInput();
  } catch (e) {
    // 取消
  }
};

const mismatchDialogVisible = ref(false);
const mismatchList = ref([]);

/** 入库前校验：返回结构化异常列表 */
const validateBeforeSubmit = () => {
  const errors = [];
  for (const row of compareList.value) {
    if (row.status === "short") {
      errors.push({
        materialNo: row.materialNo,
        type: "short",
        typeLabel: "少扫",
        expectQty: row.expectQty,
        scannedQty: row.scannedQty,
        diffText: `差${formatQty(row.expectQty - row.scannedQty)}`,
      });
    } else if (row.status === "over") {
      errors.push({
        materialNo: row.materialNo,
        type: "over",
        typeLabel: "多扫",
        expectQty: row.expectQty,
        scannedQty: row.scannedQty,
        diffText: `多${formatQty(row.scannedQty - row.expectQty)}`,
      });
    } else if (row.status === "pending") {
      errors.push({
        materialNo: row.materialNo,
        type: "pending",
        typeLabel: "未扫",
        expectQty: row.expectQty,
        scannedQty: 0,
        diffText: "",
      });
    }
  }

  const seen = new Set();
  for (const box of scannedBoxes.value) {
    if (box.materialId == null) {
      const no = String(box.materialNo || "");
      if (seen.has(no)) continue;
      seen.add(no);
      errors.push({
        materialNo: no,
        type: "error",
        typeLabel: "无ID",
        expectQty: "-",
        scannedQty: "-",
        diffText: "无法匹配物料ID",
      });
    }
  }

  return errors;
};

function mismatchTagType(type) {
  if (type === "short" || type === "pending") return "warning";
  if (type === "over" || type === "error") return "danger";
  return "info";
}

const buildSubmitPayload = () => {
  const byMaterial = new Map();
  for (const box of scannedBoxes.value) {
    const mid = box.materialId;
    if (mid == null) continue;
    if (!byMaterial.has(mid)) {
      byMaterial.set(mid, {
        materialId: mid,
        boxList: [],
      });
    }
    byMaterial.get(mid).boxList.push({
      barCode: box.barCode,
      wareOutNum: Number(box.wareOutNum) || 0,
    });
  }

  return {
    id: detail.value?.id || detail.value?.wareInId,
    wareInDetailList: Array.from(byMaterial.values()),
  };
};

const doSubmitWareIn = async () => {
  const payload = buildSubmitPayload();
  if (!payload.id) {
    ElMessage.error("缺少入库单 ID");
    return;
  }
  if (!payload.wareInDetailList.length) {
    ElMessage.error("没有可提交的物料明细");
    return;
  }

  submitting.value = true;
  try {
    const res = await tclWareInCollect(payload);
    if (res?.code !== 200) {
      throw new Error(res?.msg || "入库失败");
    }
    ElMessage.success("入库成功");
    viewMode.value = "list";
    detail.value = null;
    materialList.value = [];
    scannedBoxes.value = [];
    mismatchDialogVisible.value = false;
    mismatchList.value = [];
    if (scannedWareInNo.value) {
      await fetchByWareInNo(scannedWareInNo.value);
    } else {
      handleListReset();
    }
  } catch (e) {
    console.error("TCL入库失败:", e);
    ElMessage.error(e?.message || "入库失败，请重试");
  } finally {
    submitting.value = false;
    if (viewMode.value === "detail") focusMaterialInput();
  }
};

const handleSubmit = async () => {
  if (submitting.value) return;
  if (scannedBoxes.value.length === 0) {
    ElMessage.warning("请先扫描物料二维码");
    return;
  }
  if (hasOverQty.value) {
    playErrorBeep();
    ElMessage.error("数量超出需求，请手动点明细打开箱码详情修改后再入库");
    return;
  }

  const errors = validateBeforeSubmit().filter((e) => e.type !== "over");
  if (errors.length > 0) {
    mismatchList.value = errors;
    mismatchDialogVisible.value = true;
    return;
  }

  // 无异常也要二次确认，防误触
  try {
    await ElMessageBox.confirm(
      `已扫 ${scannedBoxCount.value} 箱，确认提交入库？`,
      "确认入库",
      {
        type: "warning",
        confirmButtonText: "确认入库",
        cancelButtonText: "取消",
        distinguishCancelAndClose: true,
      }
    );
  } catch (e) {
    return;
  }

  await doSubmitWareIn();
};

const confirmMismatchAndSubmit = async () => {
  mismatchDialogVisible.value = false;
  await doSubmitWareIn();
};

const handleBack = () => {
  if (submitting.value) return;
  if (viewMode.value === "detail") {
    viewMode.value = "list";
    detail.value = null;
    materialList.value = [];
    scannedBoxes.value = [];
    materialCode.value = "";
    focusSendInput();
    return;
  }
  router.back();
};

onMounted(() => {
  document.addEventListener("click", unlockAudio, { once: true });
  document.addEventListener("touchstart", unlockAudio, { once: true });
  focusSendInput();
});

onUnmounted(() => {
  document.removeEventListener("click", unlockAudio);
  document.removeEventListener("touchstart", unlockAudio);
  closeScanAudio();
});
</script>

<style scoped>
.page-container {
  --primary-color: #23b1c0;
  --header-background: #23b1c0;
  --header-text: #fff;
  --background-color: #f5f7fa;
  --brand: var(--primary-color);
  --ok: #67c23a;
  --warn: #e6a23c;
  --danger: #f56c6c;
  --text: #303133;
  --muted: #909399;
  --line: #ebeef5;
  --bg: var(--background-color);

  --el-color-primary: var(--primary-color);
  --el-color-primary-light-3: #4fc3cf;
  --el-color-primary-light-5: #7fd4dc;
  --el-color-primary-light-7: #afe4ea;
  --el-color-primary-light-8: #c7ecf0;
  --el-color-primary-light-9: #dff4f6;
  --el-color-primary-dark-2: #1c8e9a;

  min-height: 100vh;
  background: var(--bg);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
  padding-bottom: calc(18px + env(safe-area-inset-bottom));
}

.page-container.is-detail {
  height: 100vh;
  overflow: hidden;
  padding-bottom: 0;
}

.page-container,
.page-container * {
  box-sizing: border-box;
}

.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  padding-top: calc(10px + env(safe-area-inset-top));
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px rgba(35, 177, 192, 0.18);
  height: calc(52px + env(safe-area-inset-top));
}

.back-btn {
  border: none;
  background: transparent;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-back {
  width: 20px;
  height: 20px;
}

.title {
  flex: 1;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: var(--header-text);
}

.header-right {
  width: 36px;
}

.content {
  padding: 10px;
  margin-top: calc(52px + env(safe-area-inset-top));
}

.in-type-tabs {
  width: 100%;
  margin-bottom: 8px;
  --el-border-radius-base: 8px;
}

.block-card {
  margin-bottom: 8px;
  border-radius: 10px;
  border: 1px solid var(--line);
}

.compact-card :deep(.el-card__body) {
  padding: 10px 12px;
}

.full-btn {
  width: 100%;
}

.list-wrap {
  min-height: 180px;
}

.order-card {
  cursor: pointer;
}

.order-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.send-no {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.order-meta {
  display: block;
  margin-bottom: 2px;
}

.order-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
}

.detail-content {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 52px - env(safe-area-inset-top));
  padding-bottom: 0;
  overflow: hidden;
}

.detail-fixed {
  flex-shrink: 0;
  background: var(--bg);
  z-index: 20;
}

.detail-fixed .block-card {
  margin-bottom: 8px;
}

.detail-list-head {
  margin-bottom: 4px;
  padding: 0 2px;
}

.detail-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: calc(72px + env(safe-area-inset-bottom));
}

.scan-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.scan-row .box-input {
  flex: 1;
  min-width: 0;
}

.scan-count-tag {
  flex-shrink: 0;
}

.info-card .info-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 0;
  font-size: 13px;
}

.info-card .label {
  color: var(--muted);
  flex-shrink: 0;
}

.info-card .value {
  color: var(--text);
  text-align: right;
  word-break: break-all;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 0;
}

.section-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.compare-list {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding-bottom: 8px;
}

.compare-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  border-radius: 6px;
  background: #fafafa;
  border: 1px solid var(--line);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  white-space: nowrap;
  min-height: 32px;
}

.compare-item.ok {
  background: #f0f9eb;
  border-color: #e1f3d8;
}

.compare-item.short,
.compare-item.pending {
  background: #fdf6ec;
  border-color: #faecd8;
}

.compare-item.over {
  background: #fef0f0;
  border-color: #fde2e2;
}

.mat-idx {
  flex-shrink: 0;
  width: 18px;
  font-size: 12px;
  color: var(--muted);
  text-align: right;
}

.mat-no {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mat-qty {
  flex-shrink: 0;
  font-size: 12px;
  color: var(--muted);
}

.mat-tag {
  flex-shrink: 0;
}

.box-dialog-summary {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  margin-bottom: 10px;
  padding: 10px 8px;
  border-radius: 8px;
  background: #f5f7fa;
  border: 1px solid var(--line);
}

.box-dialog-summary.ok {
  background: #ecf9f1;
  border-color: #b7ebc6;
}

.box-dialog-summary.short {
  background: #fff8e6;
  border-color: #ffe0a3;
}

.box-dialog-summary.over {
  background: #fff1f0;
  border-color: #ffccc7;
}

.box-dialog-summary .sum-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  min-width: 0;
}

.box-dialog-summary .sum-label {
  font-size: 11px;
  color: var(--muted);
}

.box-dialog-summary .sum-val {
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.box-dialog-summary.ok .sum-diff .sum-val {
  color: #389e0d;
}

.box-dialog-summary.short .sum-diff .sum-val {
  color: #d48806;
}

.box-dialog-summary.over .sum-diff .sum-val {
  color: #cf1322;
}

.box-dialog-search {
  margin-bottom: 10px;
}

.box-dialog-scroll {
  max-height: min(50vh, 380px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-right: 2px;
}

.dialog-section {
  margin-bottom: 12px;
}

.dialog-section:last-child {
  margin-bottom: 0;
}

.dialog-section-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 8px;
}

.box-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.box-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  background: #fafafa;
  border-radius: 6px;
  border: 1px solid var(--line);
  white-space: nowrap;
  min-height: 32px;
}

.box-item.locked {
  background: #f3f6f8;
}

.box-item.highlight {
  border-color: var(--brand, #0d7377);
  background: #e8f6f6;
  box-shadow: 0 0 0 1px rgba(13, 115, 119, 0.25);
}

.box-lock-tag {
  flex-shrink: 0;
}

/* 宽度不够才省略：截前面、留尾部；够显示则完整展示 */
.box-code {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: var(--text);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  direction: rtl;
  text-align: left;
}

/* bdi 保持字符顺序不反转，不强制 ltr，否则省略会跑回尾部 */
.box-code bdi {
  unicode-bidi: isolate;
}

.box-qty {
  flex-shrink: 0;
  font-size: 12px;
  color: var(--muted);
}

.box-qty-input {
  width: 110px;
  flex-shrink: 0;
}

.box-qty-input :deep(.el-input__wrapper) {
  padding-left: 4px;
  padding-right: 28px;
}

.split-tag {
  flex-shrink: 0;
}

.box-del {
  flex-shrink: 0;
  padding: 0 4px;
}

.mismatch-summary {
  font-size: 13px;
  color: var(--muted);
  margin-bottom: 8px;
}

.mismatch-scroll {
  max-height: min(50vh, 360px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.mismatch-item {
  padding: 8px 10px;
  border-radius: 8px;
  background: #fafafa;
  border: 1px solid var(--line);
}

.mismatch-item.short,
.mismatch-item.pending {
  background: #fdf6ec;
  border-color: #faecd8;
}

.mismatch-item.over,
.mismatch-item.error {
  background: #fef0f0;
  border-color: #fde2e2;
}

.mismatch-top {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.mismatch-idx {
  flex-shrink: 0;
  width: 16px;
  font-size: 12px;
  color: var(--muted);
  text-align: right;
}

.mismatch-no {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mismatch-qty {
  margin-top: 4px;
  padding-left: 22px;
  font-size: 12px;
  color: var(--muted);
}

.qty-sep {
  margin: 0 2px;
}

.qty-diff {
  margin-left: 6px;
  color: var(--danger);
  font-weight: 600;
}

.mismatch-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
}

.footer-actions {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 90;
  padding: 10px 14px;
  padding-bottom: calc(10px + env(safe-area-inset-bottom));
  background: rgba(255, 255, 255, 0.96);
  border-top: 1px solid var(--line);
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.04);
}

.submit-btn {
  width: 100%;
  height: 44px;
  font-size: 16px;
  border-radius: 22px;
}

.submit-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.submit-panel {
  width: min(280px, 86vw);
  background: #fff;
  border-radius: 16px;
  padding: 28px 20px 24px;
  text-align: center;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
}

.submit-spinner {
  color: var(--primary-color);
  animation: spin 1s linear infinite;
}

.submit-text {
  margin: 14px 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: var(--text);
}

.submit-sub {
  margin: 0;
  font-size: 13px;
  color: var(--muted);
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
